import { useEffect, useMemo, useState } from 'react';
import type { Editor } from '@tiptap/react';
import Stack from '@mui/material/Stack';

import Icon from 'components/editor/GridDndEditor/TiptapEditor/components/Icon';
import IcoArrowDropDownRounded from 'components/icon/icons/IcoArrowDropDownRounded';
import PyMuiMenu from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenu';
import PyMuiMenuItem from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenuItem';
import PyMuiMenuLabel from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenuLabel';
import { PyMuiButton } from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents';
import { DEFAULT_FONT_SIZES, DEFAULT_SIZE } from 'components/editor/grid/reduxStore/defaultTextDefaultValue';

type Props = {
  editor: Editor | null;
  disabled?: boolean;
};

const FontSizeButton = ({ editor, disabled = false }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedValue, setSelectedValue] = useState<string>(`${DEFAULT_SIZE}px`);

  const selectedOption = useMemo(() => DEFAULT_FONT_SIZES.find((o) => o.value === selectedValue), [selectedValue]);

  useEffect(() => {
    if (!editor) return;

    const syncFontSizeFromEditor = () => {
      const value = editor.getAttributes('textStyle')?.fontSize ?? `${DEFAULT_SIZE}px`;
      setSelectedValue(value);
    };

    syncFontSizeFromEditor();
    editor.on('selectionUpdate', syncFontSizeFromEditor);
    editor.on('transaction', syncFontSizeFromEditor);
    return () => {
      editor.off('selectionUpdate', syncFontSizeFromEditor);
      editor.off('transaction', syncFontSizeFromEditor);
    };
  }, [editor]);

  const handleOpenMenu: React.MouseEventHandler<HTMLElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (value: string) => {
    editor?.chain().focus().setFontSize(value).run();
    setSelectedValue(value);
    handleClose();
  };

  return (
    <Stack direction="row" alignItems="center">
      <PyMuiButton
        testId="font-size-button"
        id="select-font-size-button"
        disabled={disabled}
        isMenuOpen={Boolean(anchorEl)}
        onClickAction={handleOpenMenu}
        width={58}
      >
        <PyMuiMenuLabel style={{ minWidth: 'auto' }}>{selectedOption?.name ?? selectedValue.replace('px', '')}</PyMuiMenuLabel>
        <Icon>
          <IcoArrowDropDownRounded />
        </Icon>
      </PyMuiButton>

      <PyMuiMenu
        testId="font-size-menu"
        id="select-font-size-menu"
        menuListProps="select-font-size-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleClose}
      >
        {DEFAULT_FONT_SIZES.map(({ name, value }) => (
          <PyMuiMenuItem
            key={name}
            testId={`font-size-menu-item-${value}`}
            onClick={() => handleSelect(value)}
            isActive={selectedValue === value}
            menuItem={name}
            customStyle={{ minWidth: 58 }}
          />
        ))}
      </PyMuiMenu>
    </Stack>
  );
};

export default FontSizeButton;
